import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import { roleHasPermission } from "@doculegis/shared/auth/permissions";
import { DEFAULT_ENABLED_APPS, type AppId, type OrgRolePermissions } from "@doculegis/shared";
import {
  authClient,
  clearJwtTokenCache,
  getJwtToken,
  getOrgPermissions,
  isJwtExpired,
  type ActiveOrganization,
  type AuthUser,
} from "@/lib/auth-client";
import { TERMS_DOCUMENT_VERSION } from "@/lib/terms-and-conditions";

export class EmailNotVerifiedError extends Error {
  readonly code = "EMAIL_NOT_VERIFIED" as const;

  constructor(
    message = "Please confirm your email first. Check your inbox for the confirmation link."
  ) {
    super(message);
    this.name = "EmailNotVerifiedError";
  }
}

interface AuthContextValue {
  isLoading: boolean;
  user: AuthUser | null;
  session: { token: string } | null;
  activeOrganization: ActiveOrganization | null;
  orgRole: string | null;
  hasOrganization: boolean;
  getAccessToken: () => Promise<string | null>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  completePasswordReset: (newPassword: string, token: string) => Promise<void>;
  resendVerificationEmail: (email: string) => Promise<void>;
  acceptTerms: () => Promise<void>;
  syncSession: () => Promise<void>;
  enabledApps: AppId[];
  hasPermission: (resource: string, action: string) => boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [session, setSession] = useState<{ token: string } | null>(null);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [activeOrganization, setActiveOrganization] = useState<ActiveOrganization | null>(null);
  const [orgRole, setOrgRole] = useState<string | null>(null);
  const [orgRolePermissions, setOrgRolePermissions] = useState<OrgRolePermissions | null>(null);
  const [enabledApps, setEnabledApps] = useState<AppId[]>([...DEFAULT_ENABLED_APPS]);

  const clearAuthState = useCallback(() => {
    clearJwtTokenCache();
    setSession(null);
    setUser(null);
    setActiveOrganization(null);
    setOrgRole(null);
    setOrgRolePermissions(null);
    setEnabledApps([...DEFAULT_ENABLED_APPS]);
  }, []);

  const syncSession = useCallback(async () => {
    try {
      const { data } = await authClient.getSession();
      const u = data?.user;
      if (u) {
        setUser({
          id: u.id,
          email: u.email,
          name: u.name,
          image: u.image,
          fullName: (u as { fullName?: string }).fullName ?? u.name,
          termsAcceptedAt: (u as { termsAcceptedAt?: Date | string | null }).termsAcceptedAt ?? null,
          termsDocumentVersion:
            (u as { termsDocumentVersion?: string | null }).termsDocumentVersion ?? null,
        });

        try {
          const jwt = await getJwtToken();
          setSession(jwt && !isJwtExpired(jwt) ? { token: jwt } : null);
        } catch {
          setSession(null);
        }

        try {
          const orgResult = await authClient.organization.getFullOrganization();
          if (orgResult.data) {
            setActiveOrganization({
              id: orgResult.data.id,
              name: orgResult.data.name,
              slug: orgResult.data.slug,
            });
          } else {
            setActiveOrganization(null);
          }
        } catch {
          setActiveOrganization(null);
        }

        try {
          const roleResult = await authClient.organization.getActiveMemberRole();
          setOrgRole(roleResult.data?.role ?? null);
        } catch {
          setOrgRole(null);
        }

        try {
          const permissions = await getOrgPermissions();
          const resolved = permissions.resolvedRolePermissions;
          setOrgRolePermissions(
            resolved && Object.keys(resolved).length > 0
              ? resolved
              : permissions.orgRolePermissions ?? {}
          );
          setEnabledApps(permissions.enabledApps ?? [...DEFAULT_ENABLED_APPS]);
        } catch {
          setOrgRolePermissions(null);
          setEnabledApps([...DEFAULT_ENABLED_APPS]);
        }
      } else {
        clearJwtTokenCache();
        setSession(null);
        setUser(null);
        setActiveOrganization(null);
        setOrgRole(null);
        setOrgRolePermissions(null);
        setEnabledApps([...DEFAULT_ENABLED_APPS]);
      }
    } catch {
      clearJwtTokenCache();
      setSession(null);
      setUser(null);
      setActiveOrganization(null);
      setOrgRole(null);
      setOrgRolePermissions(null);
      setEnabledApps([...DEFAULT_ENABLED_APPS]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    syncSession();
  }, [syncSession]);

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await authClient.signIn.email({ email, password });
    if (error) {
      if (error.status === 403) {
        throw new EmailNotVerifiedError();
      }
      throw new Error(error.message);
    }
    await syncSession();
    const { data } = await authClient.getSession();
    if (!data?.user) {
      throw new Error(
        "Signed in but session could not be loaded. Clear browser cookies and use http://localhost:5173 (not 127.0.0.1)."
      );
    }
  }, [syncSession]);

  const signOut = useCallback(async () => {
    try {
      await authClient.signOut();
    } catch {
      // Still clear local state if the network call fails.
    } finally {
      clearAuthState();
    }

    try {
      const { data } = await authClient.getSession();
      if (data?.user) {
        await authClient.signOut();
        clearAuthState();
      }
    } catch {
      // Ignore verification errors after sign-out.
    }

    navigate("/login", { replace: true });
  }, [clearAuthState, navigate]);

  const getAccessToken = useCallback(async () => getJwtToken(), []);

  const resetPassword = useCallback(async (email: string) => {
    const origin = window.location.origin;
    const { error } = await authClient.requestPasswordReset({
      email,
      redirectTo: `${origin}/reset-password`,
    });
    if (error) throw new Error(error.message);
  }, []);

  const changePassword = useCallback(async (currentPassword: string, newPassword: string) => {
    const { error } = await authClient.changePassword({
      currentPassword,
      newPassword,
      revokeOtherSessions: true,
    });
    if (error) throw new Error(error.message);
  }, []);

  const completePasswordReset = useCallback(async (newPassword: string, token: string) => {
    const { error } = await authClient.resetPassword({ newPassword, token });
    if (error) throw new Error(error.message);
  }, []);

  const resendVerificationEmail = useCallback(async (email: string) => {
    const { error } = await authClient.sendVerificationEmail({
      email,
      callbackURL: `${window.location.origin}/login`,
    });
    if (error) throw new Error(error.message);
  }, []);

  const acceptTerms = useCallback(async () => {
    const { error } = await authClient.updateUser({
      termsAcceptedAt: new Date(),
      termsDocumentVersion: TERMS_DOCUMENT_VERSION,
    } as Parameters<typeof authClient.updateUser>[0]);
    if (error) throw new Error(error.message);
    await syncSession();
  }, [syncSession]);

  const hasPermission = useCallback(
    (resource: string, action: string) => {
      if (resource === "app" && !enabledApps.includes(action as AppId)) {
        return false;
      }
      return roleHasPermission(orgRole, resource as never, action, orgRolePermissions);
    },
    [enabledApps, orgRole, orgRolePermissions]
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      isLoading,
      user,
      session,
      activeOrganization,
      orgRole,
      hasOrganization: Boolean(activeOrganization?.id),
      getAccessToken,
      signIn,
      signOut,
      resetPassword,
      changePassword,
      completePasswordReset,
      resendVerificationEmail,
      acceptTerms,
      syncSession,
      enabledApps,
      hasPermission,
    }),
    [
      isLoading,
      user,
      session,
      activeOrganization,
      orgRole,
      getAccessToken,
      signIn,
      signOut,
      resetPassword,
      changePassword,
      completePasswordReset,
      resendVerificationEmail,
      acceptTerms,
      syncSession,
      enabledApps,
      hasPermission,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}

export function usePermission(resource: string, action: string) {
  const { hasPermission } = useAuth();
  return hasPermission(resource, action);
}
